/**
 * A reserva de ônibus pelo modal-session (#4972): a pista SEM sessão. O ônibus
 * busca, lê as poltronas e o perfil direto no voll-apollo; só a ESCRITA passa
 * por aqui, para o pedido entrar na viagem antes do fornecedor (o write-ahead
 * do voo e do hotel). Separado de `session.ts` pelo teto de linhas do gate.
 */

import { z } from 'zod';

import {
  ModalSessionBookingWriteAheadSchema,
  refineWriteAheadTripLink,
} from './booking_write_ahead';
import { ModalSessionCorrelationSchema } from './correlation';
import { ModalSessionBookingSchema } from './session';

const {
  requesterId,
  travelerId,
  companyId,
  conversationId,
  resolvedTripId,
  preferredTripId,
  purchaseOrigin,
  checkoutMode,
  requestedAtWindow,
} = ModalSessionBookingWriteAheadSchema.shape;

/** O write-ahead do ônibus: os vínculos de quem pede e da viagem. O trecho
 *  (origem, destino, datas) o modal-session tira das próprias ofertas, nunca do
 *  chamador — o mesmo do pedido da bancada. */
export const ModalSessionBusBookingWriteAheadSchema = z
  .object({
    requesterId,
    travelerId,
    companyId,
    conversationId,
    resolvedTripId,
    preferredTripId,
    purchaseOrigin,
    checkoutMode,
    requestedAtWindow,
  })
  .strict()
  .superRefine(refineWriteAheadTripLink);
export type ModalSessionBusBookingWriteAhead = z.infer<
  typeof ModalSessionBusBookingWriteAheadSchema
>;

/** `{id, name}` ecoado do checkout: o bus-api recebe o par, sem lookup. */
const BusNamedRefSchema = z.object({ id: z.string().min(1), name: z.string().min(1) }).strict();

/** O teto de viajantes de uma reserva, o mesmo da busca da bancada. */
const BUS_MAX_TRAVELERS = 9;

const SeatCodesSchema = z.array(z.string().min(1)).min(1).max(BUS_MAX_TRAVELERS);

/** Uma perna: a oferta e a poltrona de cada trecho. Sem poltrona = seguir sem
 *  assento nesse trecho (a equipe VOLL escolhe). Um viajante (`traveler`) leva
 *  `seatCode`/`connectionSeatCode`; vários (`travelers`) levam `seats`/
 *  `connectionSeats`, uma poltrona por viajante, na ordem de `travelers`. */
const BusBookingLegSchema = z
  .object({
    offerId: z.string().min(1),
    seatCode: z.string().min(1).optional(),
    connectionSeatCode: z.string().min(1).optional(),
    seats: SeatCodesSchema.optional(),
    connectionSeats: SeatCodesSchema.optional(),
  })
  .strict();
type BusBookingLeg = z.infer<typeof BusBookingLegSchema>;

const BusBookingTravelerSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    email: z.string().min(1).optional(),
    birthDate: z.string().min(1).optional(),
    documents: z.array(z.object({ type: z.string().min(1), value: z.string().min(1) }).strict()),
    costCenters: z
      .array(
        z
          .object({
            percent: z.number().positive(),
            company: BusNamedRefSchema,
            costCenter: BusNamedRefSchema,
            project: BusNamedRefSchema.optional(),
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

/** A forma das poltronas acompanha a dos viajantes: um viajante usa os campos
 *  escalares; vários, uma lista por trecho com uma poltrona por viajante. */
function legSeatsFit(leg: BusBookingLeg, travelers: number | undefined): boolean {
  if (travelers === undefined) return leg.seats === undefined && leg.connectionSeats === undefined;
  if (leg.seatCode !== undefined || leg.connectionSeatCode !== undefined) return false;
  return [leg.seats, leg.connectionSeats].every(
    (codes) => codes === undefined || codes.length === travelers,
  );
}

export const ModalSessionBusBookingRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    outbound: BusBookingLegSchema,
    return: BusBookingLegSchema.optional(),
    approval: z
      .object({
        motive: BusNamedRefSchema,
        justification: BusNamedRefSchema.optional(),
        /** Observação do solicitante, lida pelo aprovador. */
        observation: z.string().max(300).optional(),
      })
      .strict(),
    /** Um viajante só — a forma de antes de a conversa reservar para vários. */
    traveler: BusBookingTravelerSchema.optional(),
    /** Vários viajantes (#5472), cada um com a sua poltrona por trecho. */
    travelers: z.array(BusBookingTravelerSchema).min(1).max(BUS_MAX_TRAVELERS).optional(),
    writeAhead: ModalSessionBusBookingWriteAheadSchema.optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    const many = request.travelers;
    if ((request.traveler === undefined) === (many === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'exactly one of traveler or travelers' });
      return;
    }
    const legs = [request.outbound, ...(request.return ? [request.return] : [])];
    if (legs.some((leg) => !legSeatsFit(leg, many?.length))) {
      ctx.addIssue({ code: 'custom', message: 'leg seats do not match the travelers' });
    }
  });
export type ModalSessionBusBookingRequest = z.infer<typeof ModalSessionBusBookingRequestSchema>;

/** `booking.status`: `pending_approval` (a reserva nasce em aprovação) ou
 *  `failed` com `outcome: 'indeterminate'` — a chamada não resolveu e o pedido
 *  pode existir, então o pedido na viagem segue aberto. */
export const ModalSessionBusBookingResponseSchema = z
  .object({
    booking: ModalSessionBookingSchema,
    correlation: ModalSessionCorrelationSchema.optional(),
  })
  .strict();
export type ModalSessionBusBookingResponse = z.infer<typeof ModalSessionBusBookingResponseSchema>;
